import { FC, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useNavigate } from "react-router-dom";
import Button from "@sumerkit/ui/button";
import TrashSimple from "@sumerkit/icons/trash-simple";
import { Loader } from "@dashboard/assets/icons";
import { useAnalytics } from "@dashboard/common/analytics";
import { useProducts } from "@dashboard/modules/products/hooks/products.hook";
import { FormProps } from "@dashboard/modules/products/types/product-form.types";
import { Product as ProductEntity } from "@dashboard/modules/products/types/products.types";
import { PRODUCTS_EVENTS } from "@dashboard/modules/products/constants/events.products";
import DeleteProductsConfirm from "../../../delete-products-confirm/delete-products-confirm";
import ServiceDetail from "../../components/service-detail/service-detail";
import ServiceInformation from "../../components/service-information/service-information";
import ServiceConfig from "../../components/service-config/service-config";
import ServiceMapContent from "../../../service-map-content/service-map-content";
import { EServiceMapContent } from "../../../service-map-content/constants/service-map-content.constants";
import {
  ProductContent,
  ProductContentWrapper,
  ProductMapContentContainer,
  ProductSaveButtonWrapper,
  ProductSaveOverlay,
} from "../product/product.styled";
import { useService } from "./service.hook";

const Service: FC = () => {
  const { t } = useTranslation();
  const serviceDetailRef = useRef<HTMLDivElement>(null);
  const serviceInformationRef = useRef<HTMLDivElement>(null);
  const serviceConfigRef = useRef<HTMLDivElement>(null);
  const [servicesToDelete, setServicesToDelete] = useState<ProductEntity[]>([]);
  const {
    handleSaveService,
    isLoading,
    saveProgressPercent,
    setSaveProgressPercent,
  } = useService();
  const { values }: { values: FormProps } = useFormikContext() ?? {};
  const { handleDeleteProducts: handleDeleteProductsFn } = useProducts();
  const { logEvent } = useAnalytics();
  const navigate = useNavigate();

  const scrollTo = (scroll: EServiceMapContent) => {
    switch (scroll) {
      case EServiceMapContent.DETAIL:
        serviceDetailRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      case EServiceMapContent.INFORMATION:
        serviceInformationRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      case EServiceMapContent.SERVICE_CONFIG:
        serviceConfigRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      default:
        serviceDetailRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleDeleteCancel = () => {
    setServicesToDelete([]);
  };

  const handleConfirmDelete = useCallback(() => {
    handleDeleteProductsFn(servicesToDelete);
    setServicesToDelete([]);
    logEvent(PRODUCTS_EVENTS.SELECT_CONFIRM_DELETE, {
      event_subtype: "delete_product",
    });
    navigate(-1);
  }, [servicesToDelete, handleDeleteProductsFn, logEvent, navigate]);

  const showSaveOverlay =
    isLoading && saveProgressPercent !== null && saveProgressPercent >= 0;
  const saveHintKey =
    saveProgressPercent !== null && saveProgressPercent < 76
      ? "admin.products.side.savingProductUploadingHint"
      : "admin.products.side.savingProductSavingHint";

  return (
    <>
      {showSaveOverlay && (
        <ProductSaveOverlay role="presentation" aria-live="polite">
          <div className="save-overlay-card">
            <Loader />
            <p className="save-overlay-title">
              {t("admin.products.side.savingProductOverlayTitle")}
            </p>
            <p className="save-overlay-hint">{t(saveHintKey)}</p>
            <div className="save-overlay-bar">
              <div
                className="save-overlay-bar-fill"
                style={{ width: `${Math.min(100, saveProgressPercent ?? 0)}%` }}
              />
            </div>
            <p className="save-overlay-percent">
              {Math.min(100, saveProgressPercent ?? 0)}%
            </p>
          </div>
        </ProductSaveOverlay>
      )}
      <ProductContentWrapper>
        <ProductContent>
          <div className="product-detail" ref={serviceDetailRef}>
            <ServiceDetail />
          </div>
          <div className="product-info" ref={serviceInformationRef}>
            <ServiceInformation />
          </div>
          <div className="product-inventory" ref={serviceConfigRef}>
            <ServiceConfig />
          </div>
        </ProductContent>
        <ProductMapContentContainer />
        <ServiceMapContent t={t} onClick={scrollTo} />
        <ProductSaveButtonWrapper>
          {values.id && (
            <div
              className="delete-container"
              onClick={() => setServicesToDelete([values as ProductEntity])}
            >
              <TrashSimple width={20} height={20} />
              <div className="text">
                {t("admin.products.side.removeButtonText")}
              </div>
            </div>
          )}
          <Button
            appearance="secondary"
            className="button"
            onClick={handleSaveService}
            size="small"
            disabled={!values.name || isLoading}
          >
            {t("admin.products.side.updateButtonText")}
          </Button>
        </ProductSaveButtonWrapper>
        {servicesToDelete.length > 0 && (
          <DeleteProductsConfirm
            products={servicesToDelete}
            onClose={handleDeleteCancel}
            onContinue={handleConfirmDelete}
          />
        )}
      </ProductContentWrapper>
    </>
  );
};

export default Service;
